import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { TextEditor, useDrawerState, DragDropContext, DragAndDropList } from '@yuhuinc/ui-library';
import { Form, Tabs, Input, Switch, Space, Typography, Button, Divider } from 'antd';
import BlockLibraryDrawer from './BlockLibraryDrawer';
import BlockConfigurationDrawer from './BlockConfigurationDrawer';
import { removeFromList, addToList, reorderList } from 'helpers/ListHelper';

import { getTemplateBlockLibrary } from '../store/actions';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTranslatedString } from 'helpers/i18n';

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const StyledSpace = styled(Space)`
  width: 100%;
  position: relative;
  top: -16px;
`;

const { Title } = Typography;
const locales = {
  en: { key: 'en', label: getTranslatedString('languages.en') },
  fr: { key: 'fr', label: getTranslatedString('languages.fr') },
};

const StyledFormItem = styled(Form.Item)`
  flex-direction: row-reverse !important;
  align-items: baseline;
  gap: 8px;
  width: fit-content;
`;

const StepForm = ({ form, formName, stepIndex = 1, stepData }) => {
  const [selectedLocale, setSelectedLocale] = useState(locales.en.key);
  const [stepBlocks, setStepBlocks] = useState(stepData?.blocks || []);
  const [blockLibrary, setBlockLibrary] = useState([]);
  const [, blockLibraryDrawer] = useDrawerState(false);
  const [, blockConfigurationDrawer] = useDrawerState(false);
  const [stepBlockForEdit, setStepBlockForEdit] = useState(null);
  const [editIndex, setEditIndex] = useState();

  useEffect(() => {
    getTemplateBlockLibrary().then(({ categories }) => setBlockLibrary(categories));
  }, []);

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) {
      return;
    }

    const isReordering =
      source.droppableId === 'STEP_BLOCKS' && destination.droppableId === 'STEP_BLOCKS';
    if (isReordering) {
      const newOrderStepBlocks = reorderList(
        stepBlocks,
        result.source.index,
        result.destination.index
      );
      setStepBlocks(newOrderStepBlocks);
      form.setFieldsValue({ blocks: newOrderStepBlocks });
    }

    const fromDrawerToStepBlocksList =
      source.droppableId !== 'STEP_BLOCKS' && destination.droppableId === 'STEP_BLOCKS';
    if (fromDrawerToStepBlocksList) {
      const sourceCategoryId = parseInt(source.droppableId);
      const prototypeBlocksList = blockLibrary.find(
        (category) => category.id === sourceCategoryId
      ).prototype_blocks;

      const [removedPrototypeBlock, newPrototypeBlocksList] = removeFromList(
        prototypeBlocksList,
        source.index
      );

      const updatedCategoriesList = blockLibrary.map((category) =>
        category.id !== sourceCategoryId
          ? category
          : { ...category, prototype_blocks: newPrototypeBlocksList }
      );

      const clonedBlock = {
        ...removedPrototypeBlock,
        prototype_block: removedPrototypeBlock,
        fields: removedPrototypeBlock.prototype_fields.map((prototypeField) => prototypeField),
      };

      const newStepBlocks = addToList(stepBlocks, destination.index, clonedBlock);
      setStepBlocks(newStepBlocks);
      form.setFieldsValue({ blocks: newStepBlocks });

      if (!removedPrototypeBlock.repeatable) {
        setBlockLibrary(updatedCategoriesList);
      }
    }
  };

  const handleRemoveBlock = (index) => {
    const [removedPrototypeBlock, newStepBlocks] = removeFromList(stepBlocks, index);
    const prototypeBlock = removedPrototypeBlock.prototype_block;
    const prototypeBlocksList = blockLibrary.find(
      (category) => category.id === prototypeBlock.category_id
    ).prototype_blocks;
    const newPrototypeBlocksList = addToList(prototypeBlocksList, 0, prototypeBlock);
    const updatedCategoriesList = blockLibrary.map((category) =>
      category.id !== prototypeBlock.category_id
        ? category
        : { ...category, prototype_blocks: newPrototypeBlocksList }
    );

    setStepBlocks(newStepBlocks);
    form.setFieldsValue({ blocks: newStepBlocks });
    setBlockLibrary(updatedCategoriesList);
  };

  const handleEditBlock = (index, stepBlock) => {
    blockLibraryDrawer.close();
    setStepBlockForEdit(stepBlock);
    setEditIndex(index);
    blockConfigurationDrawer.open();
  };

  const handleUpdateStepBlock = (stepBlockValues) => {
    const newStepBlocks = [...stepBlocks];
    const actualStepBlockData = newStepBlocks[editIndex];
    newStepBlocks[editIndex] = { ...actualStepBlockData, ...stepBlockValues };

    setStepBlocks(newStepBlocks);
    form.setFieldsValue({ blocks: newStepBlocks });
    setStepBlockForEdit(null);
    setEditIndex(null);
    blockConfigurationDrawer.close();
  };

  const CustomTextEditorInput = ({ value, onChange, editorId }) => (
    <TextEditor defaultValue={value} editorId={editorId} onChange={onChange} />
  );

  const StepFields = ({ locale }) => (
    <StyledSpace direction="vertical" size="middle">
      <Form.Item name="sorting_index" hidden>
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'title']}
        label={getTranslatedString('landlordPortal.applicationTemplate.steps.form.title')}
        rules={[requiredField]}
        hasFeedback
      >
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'description']}
        label={getTranslatedString('landlordPortal.applicationTemplate.steps.form.description')}
        requiredMark={'optional'}
        tooltip={{ title: '', icon: <InfoCircleOutlined /> }}
      >
        <CustomTextEditorInput editorId={`${locale}-step-description`} />
      </Form.Item>

      <StyledFormItem
        name={'has_rendering_question'}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.steps.form.hasRenderingQuestion'
        )}
        valuePropName="checked"
      >
        <Switch size={'small'} />
      </StyledFormItem>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.has_rendering_question !== currentValues.has_rendering_question
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('has_rendering_question') ? (
            <Form.Item
              name={['translations', locale, 'rendering_question']}
              label={getTranslatedString(
                'landlordPortal.applicationTemplate.steps.form.renderingQuestion'
              )}
              hasFeedback
              rules={[requiredField]}
            >
              <Input />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      <Form.Item name={['translations', locale, 'locale']} initialValue={locale} hidden>
        <Input />
      </Form.Item>
    </StyledSpace>
  );

  const formInitialValues = {
    has_rendering_question: stepData?.has_rendering_question || false,
    sorting_index: stepIndex,
    translations: stepData?.translations || {},
    blocks: stepData?.blocks || [],
  };

  return (
    <>
      <Title level={4}>
        {getTranslatedString('landlordPortal.applicationTemplate.steps.name', { stepIndex })}
      </Title>

      <Form
        layout="vertical"
        form={form}
        name={formName}
        initialValues={formInitialValues}
        scrollToFirstError
      >
        <Form.Item name="sorting_index" noStyle />

        <Tabs defaultActiveKey={selectedLocale} onChange={setSelectedLocale}>
          <Tabs.TabPane tab={locales.en.label} key={locales.en.key}>
            <StepFields locale={locales.en.key} />
          </Tabs.TabPane>

          <Tabs.TabPane tab={locales.fr.label} key={locales.fr.key}>
            <StepFields locale={locales.fr.key} />
          </Tabs.TabPane>
        </Tabs>

        <Form.Item noStyle hidden name={'blocks'} />
      </Form>

      <Divider />

      <Title level={5}>
        {getTranslatedString('landlordPortal.applicationTemplate.steps.form.blocks')}
      </Title>

      <DragDropContext onDragEnd={onDragEnd}>
        <DragAndDropList
          items={stepBlocks}
          listId={'STEP_BLOCKS'}
          renderItemLabel={(stepBlock) => stepBlock.translations[selectedLocale]?.title}
          placeholder={getTranslatedString('placeholderDroppableArea.placeholder')}
          onEdit={handleEditBlock}
          onDelete={handleRemoveBlock}
        />

        <Divider />

        <Button onClick={blockLibraryDrawer.open}>
          {getTranslatedString('landlordPortal.applicationTemplate.steps.addBlock')}
        </Button>

        <BlockLibraryDrawer
          isOpen={blockLibraryDrawer.isVisible}
          onClose={blockLibraryDrawer.close}
          selectedLocale={selectedLocale}
          prototypeList={blockLibrary}
        />

        {stepBlockForEdit && (
          <BlockConfigurationDrawer
            isOpen={blockConfigurationDrawer.isVisible}
            onClose={blockConfigurationDrawer.close}
            selectedLocale={selectedLocale}
            stepBlockInitialData={stepBlockForEdit}
            onUpdate={handleUpdateStepBlock}
          />
        )}
      </DragDropContext>
    </>
  );
};

export default StepForm;
